import { utc, UTCDate } from '@date-fns/utc';
import { addDays, addHours, addMinutes, addMonths, addSeconds, addWeeks, addYears, endOfMonth, getDay, isSameMonth, set, startOfMonth, startOfWeek, subWeeks, } from 'date-fns';
import * as A from 'fp-ts/lib/Array.js';
import { flow, pipe } from 'fp-ts/lib/function.js';
import * as O from 'fp-ts/lib/Option.js';
import { range } from 'ramda';
export const setInUTC = (params) => (date) => set(date, params, { in: utc });
export const getNextYear = (previousDate, params) => {
    const nextYear = addFreq(previousDate, params);
    if (!params.specificMonthOfYear &&
        !params.specificDayOfMonth &&
        !params.specificWeekOfMonth &&
        !params.specificWeekday)
        return nextYear;
    if (params.specificMonthOfYear)
        nextYear.setMonth(params.specificMonthOfYear - 1);
    if (params.specificDayOfMonth)
        nextYear.setDate(params.specificDayOfMonth);
    if (params.specificWeekOfMonth && params.specificWeekday) {
        return getSpecificMonthDay(previousDate, nextYear, {
            ...params,
            freq: 'MONTHLY',
        });
    }
    return nextYear;
};
export const getNextMonth = (previousDate, params) => {
    const nextMonth = addFreq(new UTCDate(previousDate), params);
    if (!params.specificWeekOfMonth &&
        !params.specificWeekday &&
        !params.specificDayOfMonth)
        return nextMonth;
    return getSpecificMonthDay(previousDate, nextMonth, params);
};
export const setToTargetDate = (previousDate, date, repeatEveryX) => {
    const newDate = setInUTC({
        date,
        minutes: previousDate.getUTCMinutes(),
        hours: previousDate.getUTCHours(),
        seconds: previousDate.getUTCSeconds(),
        milliseconds: previousDate.getUTCMilliseconds(),
        month: previousDate.getUTCMonth() + repeatEveryX,
    })(previousDate);
    if (newDate.getDate() !== date) {
        return setToTargetDate(addMonths(previousDate, 1), date, repeatEveryX);
    }
    return newDate;
};
const getSpecificMonthDay = (previousDate, month, params) => {
    if (params.specificDayOfMonth) {
        return setToTargetDate(previousDate, params.specificDayOfMonth, 1 * (params.repeatEveryX || 1));
    }
    const day = params.specificWeekday || 0;
    const week = params.specificWeekOfMonth || 1;
    return pipe(setToTargetWeek(month, week), (date) => {
        if (date.getDay() === day)
            return date;
        const newDate = getSpecificWeekday(date, [day === 0 ? 1 : day + 1]);
        // Edge Case:  Sometimes when we are looking for the last week of the month,
        //             our logic might place us in the first week of the next month.
        //             In this case, we need to go back a week.
        if (!isSameMonth(newDate, month))
            return subWeeks(newDate, 1);
        return newDate;
    }, setInUTC({
        minutes: previousDate.getUTCMinutes(),
        seconds: previousDate.getUTCSeconds(),
        milliseconds: previousDate.getUTCMilliseconds(),
    }));
};
export const getNextWeek = (previousDate, params) => {
    if (!params.specificWeekdays)
        return addFreq(previousDate, params);
    return getSpecificWeekday(previousDate, params.specificWeekdays);
};
export const getSpecificWeekday = (previousDate, weekdays) => {
    const offset = getOffset(previousDate);
    const daysToAdd = pipe(weekdays.sort(), A.findFirst((day) => day > offset), // Find the first weekday that is later in the week than the previous selected weekday
    O.match(() => 7 - offset + firstOrZero(weekdays), // If there is no weekday that is later in the week than the previous selected weekday
    (weekday) => weekday - offset));
    return addDays(previousDate, daysToAdd);
};
const getOffset = (date) => {
    const dayOfWeek = date.getDay();
    if (dayOfWeek === 6)
        return 0; // If it's Sunday, return 0
    return dayOfWeek + 1; // Otherwise, return the day of the week + 1
};
export const firstOrZero = flow((A.head), O.getOrElse(() => 0));
export const addFreq = (date, { freq, repeatEveryX = 1 }) => {
    const amount = 1 * repeatEveryX;
    if (freq === 'DAILY')
        return addDays(date, amount, { in: utc });
    if (freq === 'WEEKLY')
        return addWeeks(date, amount);
    if (freq === 'MONTHLY')
        return addMonths(date, amount);
    if (freq === 'YEARLY')
        return addYears(date, amount, { in: utc });
    if (freq === 'HOURLY')
        return addHours(date, amount, { in: utc });
    if (freq === 'MINUTELY')
        return addMinutes(date, amount, { in: utc });
    return addSeconds(date, amount, { in: utc });
};
/**
 * Sets the given date to the first day of the specified week of the month.
 *
 * @param {Date} month - The original date.
 * @param {number} weekOfMonth - The target week of the month (1-based, or negative to count from end).
 * @returns {Date} - A new date set to the first day of the specified week.
 */
const setToTargetWeek = (month, weekOfMonth) => {
    const targetWeek = getTargetWeekOfMonth(weekOfMonth)(month);
    if (isSameMonth(month, targetWeek))
        return targetWeek;
    return getTargetWeekOfMonth(weekOfMonth)(addMonths(month, 1));
};
const getTargetWeekOfMonth = (week) => (date) => {
    if (week < 0)
        return pipe(date, (d) => endOfMonth(d), (d) => startOfWeek(d, { weekStartsOn: 0 }), (d) => addWeeks(d, week + 1));
    return pipe(date, (d) => startOfMonth(d), (d) => addWeeks(d, week - 1));
};
const getWeekDays = (date) => {
    const start = startOfWeek(date, { weekStartsOn: 0 });
    return range(0, 6).map((offset) => addDays(start, offset));
};
// Define the main function to check if the week contains the target weekday
const weekContainsWeekday = (date, targetWeekday) => {
    return pipe(getWeekDays(date), // Get all days in the current week
    A.findFirst((day) => getDay(day) === targetWeekday && isSameMonth(day, date)), // Check for the target weekday in the same month
    O.isSome);
};
export const jsDayToRruleDay = (day) => (day === 0 ? 1 : day + 1);
//# sourceMappingURL=createDates.helpers.js.map